import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import {
  createAiPlan,
  fetchAiPlans,
  fetchAiUsage,
  fetchAiWorkAccess,
  updateAiPlan,
  updateAiWorkAccess,
} from "./aiAccessApi";
import AiQuotaReset from "./AiQuotaReset";

const periodLabel = (period) => (period === "monthly" ? "mês" : "semana");
const normalizedError = (error, fallback) => ({
  status: Number(error?.status) || 0,
  code: typeof error?.code === "string" ? error.code : "request_failed",
  retryable: error?.retryable === true,
  message: typeof error?.message === "string" ? error.message : fallback,
});
const refreshNotConfirmed = () => Object.assign(
  new Error("Não foi possível confirmar a atualização do saldo. Tente novamente."),
  {
    status: 0,
    code: "ai_usage_refresh_not_confirmed",
    retryable: true,
  },
);
const positiveLimit = (value) => {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= 1 && parsed <= 1_000_000;
};

const planDescription = (plan) => (
  plan.unlimited ? "Uso ilimitado" : `${plan.limit} por ${periodLabel(plan.period)}`
);

const shortDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "America/Sao_Paulo",
  }).format(date);
};

const periodRange = (period) => {
  const startsAt = shortDate(period.startsAt);
  const resetAt = shortDate(period.resetAt);
  return startsAt && resetAt ? `${startsAt} até ${resetAt}` : period.periodKey;
};

const consumedLabel = (value) => {
  const consumed = Number.isSafeInteger(value) ? value : 0;
  return `${consumed} ${consumed === 1 ? "análise concluída" : "análises concluídas"}`;
};

const AiUsageHistory = ({ work, refreshVersion }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState(null);
  const requestVersion = useRef(0);

  const loadUsage = async () => {
    const request = requestVersion.current + 1;
    requestVersion.current = request;
    setLoading(true);
    setError(null);
    try {
      const nextUsage = await fetchAiUsage(work.workId);
      if (request !== requestVersion.current) return;
      setUsage(nextUsage);
    } catch (caught) {
      if (request === requestVersion.current) {
        setError(normalizedError(caught, "Não foi possível carregar o histórico de uso."));
      }
    } finally {
      if (request === requestVersion.current) setLoading(false);
    }
  };

  useEffect(() => () => { requestVersion.current += 1; }, []);

  useEffect(() => {
    if (refreshVersion > 0) loadUsage();
  }, [refreshVersion]);

  const toggle = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (usage || loading) return;
    await loadUsage();
  };

  return (
    <div className="ai-usage-history">
      <button
        type="button"
        className="ai-usage-history__toggle"
        aria-expanded={open}
        aria-label={`${open ? "Ocultar" : "Ver"} histórico de uso de ${work.name}`}
        onClick={toggle}
      >
        {open ? "Ocultar histórico" : "Ver histórico de uso"}
      </button>
      {open ? (
        <section role="region" aria-label={`Histórico de uso de ${work.name}`}>
          {loading ? <p>Carregando histórico…</p> : null}
          {error ? <p className="product-access-error" role="alert">{error.message}</p> : null}
          {!loading && !error && usage?.periods?.length === 0 ? (
            <p>Nenhuma análise consumida até agora.</p>
          ) : null}
          {!loading && !error && usage?.periods?.length > 0 ? (
            <ul>
              {usage.periods.map((period) => (
                <li key={period.periodKey}>
                  <span>{periodRange(period)}</span>
                  <strong>{consumedLabel(period.consumed)}</strong>
                  <small>
                    {period.unlimited
                      ? "Plano ilimitado"
                      : `Limite do período: ${period.planLimit ?? "não informado"}`}
                    {period.reserved > 0 ? ` · ${period.reserved} em processamento` : ""}
                  </small>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}
    </div>
  );
};

const AiPlanCard = ({ plan, onReload }) => {
  const [label, setLabel] = useState(plan.label);
  const [state, setState] = useState(plan.state);
  const [period, setPeriod] = useState(plan.period);
  const [unlimited, setUnlimited] = useState(plan.unlimited);
  const [limit, setLimit] = useState(plan.limit ?? 5);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const valid = label.trim() && reason.trim() && (unlimited || positiveLimit(Number(limit)));

  const save = async () => {
    if (!valid || saving || plan.builtIn) return;
    setSaving(true);
    setError(null);
    try {
      await updateAiPlan(plan.key, {
        label: label.trim(),
        state,
        period,
        limit: unlimited ? null : Number(limit),
        unlimited,
        expectedVersion: plan.version,
        reason: reason.trim(),
      });
      await onReload();
    } catch (caught) {
      setError(normalizedError(caught, "Não foi possível atualizar o plano."));
      if (caught?.status === 409) await onReload({ preserveError: true });
    } finally {
      setSaving(false);
    }
  };

  if (plan.builtIn) {
    return (
      <fieldset className="ai-plan-card ai-plan-card--builtin" aria-label={`Plano ${plan.label}`}>
        <legend>{plan.label}</legend>
        <span className="ai-access-chip">Trial padrão</span>
        <strong>{planDescription(plan)}</strong>
        <p>Disponível permanentemente para novas obras enquanto não houver configuração própria.</p>
      </fieldset>
    );
  }

  return (
    <fieldset className="ai-plan-card" aria-label={`Plano ${plan.label}`}>
      <legend>{plan.label}</legend>
      <small>Chave {plan.key} · versão {plan.version}</small>
      <label htmlFor={`ai-plan-label-${plan.key}`}>Nome do plano {plan.key}</label>
      <input
        id={`ai-plan-label-${plan.key}`}
        value={label}
        onChange={(event) => setLabel(event.target.value)}
      />
      <div className="ai-plan-grid">
        <label>
          Estado
          <select value={state} onChange={(event) => setState(event.target.value)}>
            <option value="active">Ativo</option>
            <option value="inactive">Inativo</option>
          </select>
        </label>
        <label>
          Período
          <select value={period} onChange={(event) => setPeriod(event.target.value)}>
            <option value="weekly">Semanal</option>
            <option value="monthly">Mensal</option>
          </select>
        </label>
      </div>
      <label className="ai-access-check">
        <input
          type="checkbox"
          checked={unlimited}
          onChange={(event) => setUnlimited(event.target.checked)}
        />
        Uso ilimitado
      </label>
      {!unlimited ? (
        <label>
          Limite
          <input
            type="number"
            min="1"
            max="1000000"
            value={limit}
            onChange={(event) => setLimit(event.target.value)}
          />
        </label>
      ) : null}
      <label htmlFor={`ai-plan-reason-${plan.key}`}>Motivo da alteração</label>
      <textarea
        id={`ai-plan-reason-${plan.key}`}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />
      {error ? <p className="product-access-error" role="alert">{error.message}</p> : null}
      <button type="button" disabled={!valid || saving} onClick={save}>
        {saving ? "Salvando…" : "Salvar plano"}
      </button>
    </fieldset>
  );
};

const NewAiPlanForm = ({ onReload }) => {
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [period, setPeriod] = useState("weekly");
  const [unlimited, setUnlimited] = useState(false);
  const [limit, setLimit] = useState(5);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const validKey = /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/.test(key);
  const valid = validKey && label.trim() && reason.trim() && (unlimited || positiveLimit(Number(limit)));

  const save = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setError(null);
    try {
      await createAiPlan({
        key,
        label: label.trim(),
        state: "active",
        period,
        limit: unlimited ? null : Number(limit),
        unlimited,
        reason: reason.trim(),
      });
      setKey("");
      setLabel("");
      setPeriod("weekly");
      setUnlimited(false);
      setLimit(5);
      setReason("");
      await onReload();
    } catch (caught) {
      setError(normalizedError(caught, "Não foi possível criar o plano."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <fieldset className="ai-plan-card ai-plan-card--new" aria-label="Novo plano de IA">
      <legend>Novo plano</legend>
      <label htmlFor="new-ai-plan-key">Chave do plano</label>
      <input
        id="new-ai-plan-key"
        value={key}
        onChange={(event) => setKey(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
        placeholder="starter-weekly"
      />
      <label htmlFor="new-ai-plan-label">Nome do plano</label>
      <input id="new-ai-plan-label" value={label} onChange={(event) => setLabel(event.target.value)} />
      <label htmlFor="new-ai-plan-period">Período do plano</label>
      <select id="new-ai-plan-period" value={period} onChange={(event) => setPeriod(event.target.value)}>
        <option value="weekly">Semanal</option>
        <option value="monthly">Mensal</option>
      </select>
      <label className="ai-access-check">
        <input
          type="checkbox"
          checked={unlimited}
          onChange={(event) => setUnlimited(event.target.checked)}
        />
        Uso ilimitado no plano
      </label>
      {!unlimited ? (
        <label htmlFor="new-ai-plan-limit">Limite do plano</label>
      ) : null}
      {!unlimited ? (
        <input
          id="new-ai-plan-limit"
          type="number"
          min="1"
          max="1000000"
          value={limit}
          onChange={(event) => setLimit(event.target.value)}
        />
      ) : null}
      <label htmlFor="new-ai-plan-reason">Motivo da criação</label>
      <textarea id="new-ai-plan-reason" value={reason} onChange={(event) => setReason(event.target.value)} />
      {error ? <p className="product-access-error" role="alert">{error.message}</p> : null}
      <button type="button" disabled={!valid || saving} onClick={save}>
        {saving ? "Criando…" : "Criar plano"}
      </button>
    </fieldset>
  );
};

const WorkAiAccessForm = ({ companyId, work, plans, onReload }) => {
  const [enabled, setEnabled] = useState(work.enabled);
  const [planKey, setPlanKey] = useState(work.plan.key);
  const [overrideEnabled, setOverrideEnabled] = useState(work.source === "work_override");
  const [period, setPeriod] = useState(work.plan.period);
  const [unlimited, setUnlimited] = useState(work.plan.unlimited);
  const [limit, setLimit] = useState(work.plan.limit ?? 5);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [historyRefreshVersion, setHistoryRefreshVersion] = useState(0);
  const valid = reason.trim() && (!overrideEnabled || unlimited || positiveLimit(Number(limit)));
  const activePlans = plans.filter((plan) => plan.state === "active" || plan.key === planKey);
  const sourceLabel = work.source === "default"
    ? "Trial padrão"
    : work.source === "work_override"
      ? "Cota personalizada"
      : "Plano da obra";

  const selectPlan = (nextKey) => {
    setPlanKey(nextKey);
    if (!overrideEnabled) {
      const next = plans.find((plan) => plan.key === nextKey);
      if (next) {
        setPeriod(next.period);
        setUnlimited(next.unlimited);
        setLimit(next.limit ?? 5);
      }
    }
  };

  const toggleOverride = (nextEnabled) => {
    setOverrideEnabled(nextEnabled);
    if (nextEnabled) {
      const selected = plans.find((plan) => plan.key === planKey);
      if (selected) {
        setPeriod(selected.period);
        setUnlimited(selected.unlimited);
        setLimit(selected.limit ?? 5);
      }
    }
  };

  const save = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setError(null);
    try {
      await updateAiWorkAccess(work.workId, {
        state: enabled ? "active" : "off",
        planKey,
        quotaOverride: overrideEnabled ? {
          period,
          limit: unlimited ? null : Number(limit),
          unlimited,
        } : null,
        expectedVersion: work.version,
        reason: reason.trim(),
      });
      await onReload();
    } catch (caught) {
      setError(normalizedError(caught, "Não foi possível atualizar a IA desta obra."));
      if (caught?.status === 409) await onReload({ preserveError: true });
    } finally {
      setSaving(false);
    }
  };

  const reloadAfterReset = async () => {
    const reloaded = await onReload({ throwOnError: true });
    if (reloaded !== true) throw refreshNotConfirmed();
    setHistoryRefreshVersion((current) => current + 1);
  };

  return (
    <fieldset className="ai-work-card" aria-label={`IA da obra ${work.name}`}>
      <legend>
        <b>{work.name}</b>
        <span>{sourceLabel} · versão {work.version}</span>
      </legend>
      <div className="ai-work-card__summary">
        <span className={`ai-access-chip${enabled ? " ai-access-chip--on" : ""}`}>
          {enabled ? "IA ativa" : "IA desativada"}
        </span>
        {work.quota?.unlimited ? <strong>Uso ilimitado</strong> : null}
        {work.quota && !work.quota.unlimited ? (
          <strong>{work.quota.remaining} de {work.quota.limit} usos disponíveis</strong>
        ) : null}
      </div>
      <p className="ai-work-card__shared">A cota é compartilhada por todos os usuários desta obra.</p>
      <AiUsageHistory work={work} refreshVersion={historyRefreshVersion} />
      <AiQuotaReset
        companyId={companyId}
        work={work}
        usage={work.quota}
        onReset={reloadAfterReset}
      />

      <label className="ai-access-check">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => setEnabled(event.target.checked)}
          aria-label={`IA ativa em ${work.name}`}
        />
        Habilitar IA dentro do Ampli Obras
      </label>

      <label htmlFor={`ai-work-plan-${work.workId}`}>Plano de {work.name}</label>
      <select
        id={`ai-work-plan-${work.workId}`}
        value={planKey}
        onChange={(event) => selectPlan(event.target.value)}
      >
        {activePlans.map((plan) => (
          <option key={plan.key} value={plan.key}>{plan.label} · {planDescription(plan)}</option>
        ))}
      </select>

      <label className="ai-access-check">
        <input
          type="checkbox"
          checked={overrideEnabled}
          onChange={(event) => toggleOverride(event.target.checked)}
          aria-label={`Personalizar cota de ${work.name}`}
        />
        Personalizar a cota somente desta obra
      </label>

      {overrideEnabled ? (
        <div className="ai-work-card__quota">
          <label htmlFor={`ai-work-period-${work.workId}`}>Período da cota de {work.name}</label>
          <select
            id={`ai-work-period-${work.workId}`}
            value={period}
            onChange={(event) => setPeriod(event.target.value)}
          >
            <option value="weekly">Semanal</option>
            <option value="monthly">Mensal</option>
          </select>
          <label className="ai-access-check ai-access-check--quota">
            <input
              type="checkbox"
              checked={unlimited}
              onChange={(event) => setUnlimited(event.target.checked)}
              aria-label={`Uso ilimitado em ${work.name}`}
            />
            Uso ilimitado
          </label>
          {!unlimited ? (
            <>
              <label htmlFor={`ai-work-limit-${work.workId}`}>Limite de análises de {work.name}</label>
              <input
                id={`ai-work-limit-${work.workId}`}
                type="number"
                min="1"
                max="1000000"
                value={limit}
                onChange={(event) => setLimit(event.target.value)}
              />
            </>
          ) : null}
        </div>
      ) : null}

      <label htmlFor={`ai-work-reason-${work.workId}`}>Motivo da alteração de {work.name}</label>
      <textarea
        id={`ai-work-reason-${work.workId}`}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder="Ex.: contratação do plano Pro"
      />
      {error ? <p className="product-access-error" role="alert">{error.message}</p> : null}
      <button type="button" disabled={!valid || saving} onClick={save} aria-label={`Salvar IA de ${work.name}`}>
        {saving ? "Salvando…" : "Salvar configuração"}
      </button>
    </fieldset>
  );
};

const AiWorkAccessManager = ({ companyId }) => {
  const [plans, setPlans] = useState(null);
  const [works, setWorks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestVersion = useRef(0);
  const mounted = useRef(false);
  const activeCompanyId = useRef(companyId);

  const load = async ({ preserveError = false, throwOnError = false } = {}) => {
    const requestedCompanyId = companyId;
    if (!mounted.current || activeCompanyId.current !== requestedCompanyId) return false;
    const request = requestVersion.current + 1;
    requestVersion.current = request;
    const isCurrent = () => (
      mounted.current
      && request === requestVersion.current
      && requestedCompanyId === activeCompanyId.current
    );
    setLoading(true);
    if (!preserveError) setError(null);
    try {
      const [plansResult, worksResult] = await Promise.all([
        fetchAiPlans(),
        fetchAiWorkAccess(companyId),
      ]);
      if (!isCurrent()) return false;
      setPlans(plansResult.plans ?? []);
      setWorks(worksResult.works ?? []);
      return true;
    } catch (caught) {
      if (isCurrent()) {
        setError(normalizedError(caught, "Não foi possível carregar a IA por obra."));
        if (throwOnError) throw caught;
      }
      return false;
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      requestVersion.current += 1;
    };
  }, []);

  useLayoutEffect(() => {
    if (activeCompanyId.current === companyId) return;
    activeCompanyId.current = companyId;
    requestVersion.current += 1;
  }, [companyId]);

  useEffect(() => {
    load();
    return () => { requestVersion.current += 1; };
  }, [companyId]);

  return (
    <section className="ai-access-manager" aria-label="IA contextual por obra">
      <header>
        <div>
          <p className="product-access-eyebrow">Ampli Obras com IA</p>
          <h3>IA contextual por obra</h3>
          <p>O trial padrão oferece cinco análises por semana. A cota pertence à obra, não ao usuário.</p>
        </div>
        <button type="button" onClick={() => load()} disabled={loading}>Recarregar IA</button>
      </header>

      {loading && plans === null ? <p>Carregando planos e obras…</p> : null}
      {error ? <p className="product-access-error" role="alert">{error.message}</p> : null}

      {plans ? (
        <section className="ai-plans" aria-label="Planos de IA">
          <h4>Planos comerciais</h4>
          <div className="ai-plans__grid">
            {plans.map((plan) => (
              <AiPlanCard
                key={`${plan.key}-${plan.version}`}
                plan={plan}
                onReload={load}
              />
            ))}
            <NewAiPlanForm onReload={load} />
          </div>
        </section>
      ) : null}

      {works ? (
        <section className="ai-works" aria-label="Configuração de IA das obras">
          <h4>Configuração por obra</h4>
          {works.length === 0 ? <p>Nenhuma obra ativa encontrada para esta empresa.</p> : null}
          <div className="ai-works__grid">
            {works.map((work) => (
              <WorkAiAccessForm
                key={`${work.workId}-${work.version}-${work.source}`}
                companyId={companyId}
                work={work}
                plans={plans ?? []}
                onReload={load}
              />
            ))}
          </div>
        </section>
      ) : null}
    </section>
  );
};

export default AiWorkAccessManager;
